import { useEffect, useState } from "react";
import { PageHeader, Stat, Empty } from "@/components/UIBits";
import api from "@/lib/api";
import { Link } from "react-router-dom";

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.stats(), api.listLogs(12)]);
      setStats(s);
      setRecent(l);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="overview-page">
      <PageHeader
        title="Control Room"
        subtitle="// Live system telemetry"
        testid="overview-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-10">
        <Stat label="Users" value={stats?.users ?? "—"} testid="stat-users" />
        <Stat label="Pool" value={stats?.pool_size ?? "—"} testid="stat-pool" />
        <Stat
          label="Active Maps"
          value={stats?.active_mappings ?? "—"}
          accent="text-emerald-400"
          testid="stat-active"
        />
        <Stat
          label="Burned"
          value={stats?.burned_mappings ?? "—"}
          accent="text-red-400"
          testid="stat-burned"
        />
        <Stat
          label="Blocked"
          value={stats?.burn_list_size ?? "—"}
          accent="text-red-400"
          testid="stat-blocked"
        />
        <Stat
          label="vCards"
          value={stats?.vcards_generated ?? "—"}
          testid="stat-vcards"
        />
      </div>

      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="font-heading text-xl text-white">Recent Activity</h2>
        <Link
          to="/admin/logs"
          data-testid="open-full-logs"
          className="font-mono text-xs text-zinc-500 hover:text-white uppercase tracking-widest"
        >
          [open full feed →]
        </Link>
      </div>

      {recent.length === 0 ? (
        <Empty testid="overview-empty">No activity yet — try the simulator</Empty>
      ) : (
        <div className="terminal-feed p-4 max-h-[420px] overflow-y-auto" data-testid="overview-feed">
          {recent.map((l) => (
            <LogRow key={l.id} log={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function LogRow({ log }) {
  const dirColor = {
    inbound: "text-blue-400",
    outbound: "text-emerald-400",
    system: "text-zinc-400",
    blocked: "text-red-400",
    burn: "text-red-500",
    vcard: "text-amber-400",
  }[log.direction] || "text-zinc-400";

  const ts = new Date(log.timestamp).toLocaleTimeString();
  return (
    <div className="line py-1 px-2 flex gap-3 items-start">
      <span className="text-zinc-600 shrink-0">{ts}</span>
      <span className={`shrink-0 uppercase ${dirColor}`}>
        [{log.direction}]
      </span>
      <span className="text-zinc-300 truncate">
        {log.from_number && <span className="text-zinc-500">{log.from_number}</span>}
        {log.to_number && <span className="text-zinc-500"> → {log.to_number}</span>}
        {log.text && <span className="text-white"> · {log.text}</span>}
        {log.note && <span className="text-zinc-500"> // {log.note}</span>}
      </span>
    </div>
  );
}
